import type { SocialPostPreviewCardMetadata, SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';

// Which held social post a waiting task card shows, and what its compact form draws. Pure and type-only
// imports, so a unit test can call it without the app graph (see CLAUDE.md "Tests").
//
// The held post rides the task as `socialPostPreviewCard` (the harness writes it on `display` when an agent
// holds a post for the owner's OK — ActHolds.java). Every surface that shows the question shows the post
// above it, and only while that question is still open: the card names its `decisionId`, and a post whose
// question was answered here, or that no prompt on this card asks, is not the reader's to look at any more.
// Same rule as iOS `AgentTaskCard.postPreview` and Android `agentTaskCardSocialPost`.

/**
 * The held post to draw over a waiting card's question, or `undefined`.
 *
 * `prompts` are the questions THIS card answers — the task's own on the list row and the drill-in, the one
 * grouped prompt on an island question card — so a post is never drawn over a question that is not about it.
 */
export function postPreviewHoldsDecision(
  card: SocialPostPreviewCardMetadata | undefined,
  prompts: readonly { decisionId?: string }[] | undefined,
  answeredDecisionIds: readonly string[],
): SocialPostPreviewCardMetadata | undefined {
  if (!card || answeredDecisionIds.includes(card.decisionId)) return undefined;
  return prompts?.some((prompt) => prompt.decisionId === card.decisionId) ? card : undefined;
}

/** What the island's COMPACT preview draws: one thumbnail, the network, and the caption (clamped to two lines
 *  by CSS). Nothing in it is a control, so a tap on it falls through to the card and opens the task. */
export interface CompactPostPreview {
  network: string;
  /** The account the post goes out as, falling back to the network's name. */
  title: string;
  /** The first media item, when the post has one the client can show. */
  thumb?: SocialPostPreviewMedia;
  caption?: string;
}

export function compactPostPreview(card: SocialPostPreviewCardMetadata): CompactPostPreview {
  const thumb = card.media[0];
  const caption = card.caption?.trim();
  return {
    network: card.network,
    title: card.accountName ?? card.accountLabel ?? card.networkLabel,
    ...(thumb ? { thumb } : {}),
    ...(caption ? { caption } : {}),
  };
}
